import moment from 'moment';
import { useCallback, useState } from 'react';
import {
  BooleanField,
  Button,
  ChipField,
  Datagrid,
  DateField,
  DateInput,
  Filter,
  List,
  NumberField,
  ReferenceArrayField,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  SingleFieldList,
  TextField,
  TopToolbar,
  useDataProvider,
  useGetIdentity,
  useListContext,
  useNotify,
  useRefresh,
  useUnselectAll,
} from 'react-admin';
import { Alert, Box, Tooltip } from '@mui/material';
import { CustomizableDatagrid, Permissions } from '../components';
import { PERMISSIONS } from '../constants';
import { useRowClick } from '../hooks/useRowClick';
import { UserRole } from '../providers/authProvider';
import { getChainId } from '../utils/utils';

const SelectAllButton = () => {
  const { filterValues, onSelect } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const { identity } = useGetIdentity();

  const handleSelectAll = useCallback(async () => {
    setLoading(true);
    try {
      const filterValue = identity?.role === UserRole.chain ? { chainId: getChainId() } : {};

      // Buscar todos os IDs que atendem aos filtros
      const { data: allRecords } = await dataProvider.getList('fillins', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'receivedAt', order: 'DESC' },
        filter: {
          ...filterValue,
          ...filterValues,
          hasInvoice: true,
          hasSubCompanyBilling: false,
          status: 'approved',
          stationReviewStatus: 'approved',
          paymentMethod: 'pump-x',
        }
      });

      // Filtrar apenas os que podem ser selecionados (data de faturamento não é futura)
      const selectableIds = allRecords
        .filter(record => {
          if (!record.chainCompanyBillingDueDate) return true;
          return moment(record.chainCompanyBillingDueDate).isSameOrBefore(moment(), 'day');
        })
        .map(record => record.id);

      // Selecionar todos os IDs
      selectableIds.forEach(id => onSelect([id]));
      notify(`${selectableIds.length} registros selecionados`, { type: 'info' });
    } catch (error) {
      console.log("🚀 ~ SelectAllButton ~ error:", error)
      notify('Erro ao selecionar todos os registros', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [dataProvider, filterValues, onSelect, notify, identity]);

  return (
    <Button
      label="Selecionar Todos"
      onClick={handleSelectAll}
      disabled={loading}
    />
  );
};

const BulkGenerateBillingButton = () => {
  const { selectedIds } = useListContext();
  const refresh = useRefresh();
  const notify = useNotify();
  const unselectAll = useUnselectAll('fillins');
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(async () => {
    if (selectedIds.length === 0) {
      notify('Selecione pelo menos um abastecimento', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      await dataProvider.create('fillins/generate-subcompany-billing', {
        data: { fillinIds: selectedIds }
      });
      notify('Faturamento gerado com sucesso', { type: 'success' });
      unselectAll();
      refresh();
    } catch (error) {
      notify(error.message || 'Erro ao gerar faturamento', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedIds, dataProvider, notify, refresh, unselectAll]);

  return (
    <Button
      label="Gerar Faturamento"
      onClick={handleClick}
      disabled={loading || selectedIds.length === 0}
    />
  );
};

const ListFilters = (props) => {
  const { identity } = useGetIdentity();

  return (
    <Filter {...props}>
      <DateInput
        source="from"
        label="De"
        alwaysOn
        parse={(value) => moment(value).startOf('day').toISOString()}
      />
      <DateInput
        source="to"
        label="Até"
        alwaysOn
        parse={(value) => moment(value).endOf('day').toISOString()}
      />
      {identity?.role === UserRole.admin && (
        <ReferenceInput
          label="Rede"
          source="chainId"
          reference="chains"
          sort={{ field: 'name', order: 'ASC' }}
          alwaysOn
        >
          <SelectInput label="Rede" optionText="name" />
        </ReferenceInput>
      )}
      <ReferenceArrayInput
        source="stationIds"
        reference="stations"
        sort={{ field: 'name', order: 'ASC' }}
        filter={identity?.role === UserRole.chain ? { chainId: getChainId() } : undefined}
        alwaysOn
        perPage={null}
      >
        <SelectArrayInput label="Posto" optionText="name" />
      </ReferenceArrayInput>
      <ReferenceInput
        label="Transportadora"
        source="subCompanyId"
        reference="sub-companies"
        sort={{ field: 'name', order: 'ASC' }}
        alwaysOn
      >
        <SelectInput label="Transportadora" optionText="name" />
      </ReferenceInput>
    </Filter>
  );
};

const ListBulkActions = () => (
  <>
    {/* <SelectAllButton /> */}
    <BulkGenerateBillingButton />
  </>
);

const InfoAlert = () => (
  <Box sx={{ mb: 2 }}>
    <Alert severity="info">
      Abastecimentos com data de faturamento futura não podem ser selecionados.
      Apenas registros com data de faturamento igual ou anterior à data atual podem ser faturados.
    </Alert>
  </Box>
);

const DatagridWithTooltip = ({ children, isRowSelectable, ...props }) => {
  const RowWithTooltip = ({ record, ...rowProps }) => {
    const isSelectable = isRowSelectable(record);
    const tooltipTitle = !isSelectable
      ? `Data de faturamento: ${moment(record.chainCompanyBillingDueDate).format('DD/MM/YYYY')} - Aguarde a data de faturamento para selecionar`
      : '';

    return (
      <Tooltip title={tooltipTitle} arrow placement="left">
        <tr {...rowProps} style={{ opacity: !isSelectable ? 0.6 : 1 }} />
      </Tooltip>
    );
  };

  return (
    <CustomizableDatagrid
      {...props}
      isRowSelectable={isRowSelectable}
      row={<RowWithTooltip />}
    >
      {children}
    </CustomizableDatagrid>
  );
};

export const PendingSubCompanyBillingList = () => {
  const { identity } = useGetIdentity();
  const { handleRowClick } = useRowClick();

  const filterValue = identity?.role === UserRole.chain ? { chainId: getChainId() } : {};

  const isRowSelectable = (record) => {
    if (!record.chainCompanyBillingDueDate) return true;
    return moment(record.chainCompanyBillingDueDate).isSameOrBefore(moment(), 'day');
  };

  return (
    <List
      resource="fillins"
      filter={{
        ...filterValue,
        hasInvoice: true,
        hasSubCompanyBilling: false,
        status: 'approved',
        stationReviewStatus: 'approved',
        paymentMethod: 'pump-x',
      }}
      filters={<ListFilters />}
      filterDefaultValues={{
        from: moment().subtract(30, 'days').startOf('day').toISOString(),
        to: moment().endOf('day').toISOString(),
      }}
      sort={{ field: 'receivedAt', order: 'DESC' }}
      perPage={25}
      title="Pendências de Faturamento - Transportadoras"
    >
      <InfoAlert />
      <DatagridWithTooltip
        rowClick={(id, resource, record) => handleRowClick('show', id, resource, record)}
        bulkActionButtons={<ListBulkActions />}
        isRowSelectable={isRowSelectable}
        defaultColumns={['receivedAt', 'chainCompanyBillingDueDate', 'stationId', 'chainId', 'subCompanyId', 'value', 'amount', 'invoiceId']}
      >
        <DateField source="receivedAt" label="Data" showTime locales="pt-BR" />

        <DateField source="chainCompanyBillingDueDate" label="Data de Faturamento" locales="pt-BR" />

        <ReferenceField
          label="Posto"
          source="stationId"
          reference="stations"
          link={identity?.role === UserRole.admin ? 'show' : false}
        >
          <TextField source="name" />
        </ReferenceField>

        <Permissions userRoles={[UserRole.admin]}>
          <ReferenceField
            label="Rede"
            source="chainId"
            reference="chains"
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        </Permissions>

        <ReferenceField
          label="Transportadora"
          source="subCompanyId"
          reference="sub-companies"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>

        <ReferenceField
          label="Veículo"
          source="vehicleId"
          reference="vehicles"
          link={false}
        >
          <TextField source="licensePlate" />
        </ReferenceField>

        <ReferenceField
          label="Combustível"
          source="fuelId"
          reference="fuels"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>

        <NumberField
          source="amount"
          label="Litros"
          locales="pt-BR"
          options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />

        <NumberField
          source="value"
          label="Valor"
          locales="pt-BR"
          options={{ style: 'currency', currency: 'BRL' }}
        />

        <ReferenceField
          label="Nota Fiscal"
          source="invoiceId"
          reference="invoices"
          link={false}
        >
          <TextField source="invoiceNumber" />
        </ReferenceField>
      </DatagridWithTooltip>
    </List>
  );
};

export default PendingSubCompanyBillingList;
